import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

export default function LoginPage() {
  const { signIn, demoBypass } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── MT Demo Token Auto-Auth (stateless HMAC) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoToken = params.get('demo_token');
    if (!demoToken || !demoToken.includes('.')) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    const MT_SECRET = 'mt-sovereign-demo-2026-blacksmith';
    const s2b = s => new TextEncoder().encode(s);
    const b64d = s => {
      s = s.replace(/-/g,'+').replace(/_/g,'/');
      while(s.length%4) s+='=';
      const b = atob(s);
      const a = new Uint8Array(b.length);
      for(let i=0;i<b.length;i++) a[i]=b.charCodeAt(i);
      return a;
    };
    const b64e = buf => {
      const b = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
      let r=''; b.forEach(x=>r+=String.fromCharCode(x));
      return btoa(r).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
    };

    (async () => {
      try {
        const [payloadStr, sig] = demoToken.split('.');
        const key = await crypto.subtle.importKey('raw', s2b(MT_SECRET), {name:'HMAC',hash:'SHA-256'}, false, ['sign']);
        const expected = b64e(new Uint8Array(await crypto.subtle.sign('HMAC', key, s2b(payloadStr))));
        if (expected !== sig) { console.warn('[DemoToken] Invalid signature'); return; }

        const payload = JSON.parse(new TextDecoder().decode(b64d(payloadStr)));
        if (Date.now() > payload.exp) { console.warn('[DemoToken] Token expired'); return; }

        // Auto-authenticate: set user in localStorage and reload
        const email = payload.email || 'demo@miskulintechnologies.com';
        const role = payload.role || 'owner';

        // Try using the AuthContext demoBypass if available
        if (typeof demoBypass === 'function') {
          demoBypass(email, role);
        } else {
          // Direct localStorage injection — works with any MT app
          // Find the app's localStorage prefix
          const keys = Object.keys(localStorage);
          const prefixKey = keys.find(k => k.endsWith('_user'));
          const prefix = prefixKey ? prefixKey.replace('_user', '') : 'mt';

          localStorage.setItem(prefix + '_user', JSON.stringify({
            email, full_name: email.split('@')[0], role, isOwner: role === 'owner', isDemoSession: true
          }));
          localStorage.setItem(prefix + '_onboarding_step', 'null');
          localStorage.setItem(prefix + '_password_changed', 'true');
          localStorage.setItem(prefix + '_nda_signed', 'true');
          localStorage.setItem(prefix + '_tutorial_complete', 'true');
          window.location.reload();
        }
      } catch (e) { console.warn('[DemoToken] Auto-auth failed:', e); }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Invalid login credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = () => {
    demoBypass();
    navigate('/');
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        width: '100%', maxWidth: 420, padding: 40, background: 'var(--card)',
        borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28, fontWeight: 700, color: '#fff'
          }}>
            {('The Forge Standard').charAt(0)}
          </div>
          <h1 style={{ color: 'var(--text)', fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>
            The Forge Standard
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            Sign in to your account
          </p>
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: 20, borderRadius: 8,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', fontSize: 13
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Email Address
            </label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={{
                width: '100%', padding: '10px 14px', background: 'var(--bg)',
                border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
                fontSize: 14, outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Password
            </label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              style={{
                width: '100%', padding: '10px 14px', background: 'var(--bg)',
                border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
                fontSize: 14, outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px 0', background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              marginBottom: 12
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div onClick={() => window.location.href = '/forgot-password'} style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>Forgot your password?</div>

        <button
          onClick={handleDemo}
          style={{
            width: '100%', padding: '10px 0', background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)',
            fontSize: 13, cursor: 'pointer', fontWeight: 500
          }}
        >
          Presentation Mode
        </button>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, marginTop: 24, marginBottom: 0 }}>
          Powered by Miskulin Technologies
        </p>
      </div>
    </div>
  );
}
